import axios, {
    type AxiosInstance,
    type AxiosResponse,
    type AxiosError,
} from 'axios';
import type { HttpProvider, HttpRequestConfig } from '../http-provider';
import { HttpStatus } from '@/api/status-codes';
import { ApiError, ApiErrorDetail } from '@/api/http-error';

type DataAxiosInstance = Omit<AxiosInstance, keyof HttpProvider> & HttpProvider;

function createInstance(): DataAxiosInstance {
    const instance = axios.create();

    instance.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

    instance.interceptors.response.use((response: AxiosResponse) => {
        if (response.status === HttpStatus.Ok
            && response.headers.location
            && response.data.redirect
        ) {
            window.location.href = response.data.redirect;
            return;
        }

        return response.data;
    }, (error: AxiosError) => {
        const response = error.response;
        const detail = (response?.data as any)?.detail as ApiErrorDetail | undefined;

        return Promise.reject(new ApiError({
            error,
            response,
            status: response?.status ?? HttpStatus.InternalServerError,
            statusText: response?.statusText ?? 'Internal Server Error',
            data: detail?.data ?? [],
            message: detail?.message ?? error.message,
            fields: detail?.fields ?? [],
            code: detail?.code,
        }));
    });

    return instance;
}

function onReject(error: ApiError | Error): Promise<never> {
    if (!(error instanceof ApiError)) return Promise.reject(error);

    if (error.status === HttpStatus.Unauthorized) {
        window.location.href = '/user/login'; // TODO DATATOCRM-15581
        return Promise.reject(error);
    }

    if (error.status === HttpStatus.Forbidden) {
        window.location.href = '/'; // TODO DATATOCRM-15581
        return Promise.reject(error);
    }

    return Promise.reject(error);
}

export default class Axios implements HttpProvider {
    private readonly _instance: DataAxiosInstance;

    constructor() {
        this._instance = createInstance();
    }

    get instance(): DataAxiosInstance {
        return this._instance;
    }

    async get<T = unknown>(
        url: string,
        config?: HttpRequestConfig,
    ): Promise<T> {
        try {
            return await this._instance.get<T>(url, config);
        } catch (error: ApiError | any) {
            return onReject(error);
        }
    }

    async post<T = unknown>(
        url: string,
        data?: unknown,
        config?: HttpRequestConfig,
    ): Promise<T> {
        try {
            return await this._instance.post<T>(url, data, config);
        } catch (error: ApiError | any) {
            return onReject(error);
        }
    }

    async put<T = unknown>(
        url: string,
        data?: unknown,
        config?: HttpRequestConfig,
    ): Promise<T> {
        try {
            return await this._instance.put<T>(url, data, config);
        } catch (error: ApiError | any) {
            return onReject(error);
        }
    }

    async delete<T = unknown>(
        url: string,
        config?: HttpRequestConfig,
    ): Promise<T> {
        try {
            return await this._instance.delete<T>(url, config);
        } catch (error: ApiError | any) {
            return onReject(error);
        }
    }
}
